import { useState, useCallback, useEffect, useRef } from 'react';
import '@/App.css';
import { Toaster } from './components/ui/sonner';
import { Sidebar } from './components/Sidebar';
import { Dashboard } from './pages/Dashboard';
import { Conversations } from './pages/Conversations';
import { Attendants } from './pages/Attendants';
import { Contacts } from './pages/Contacts';
import InstancesEvolution from './pages/InstancesEvolution';
import { Integrations } from './pages/Integrations';
import { WebhookLogs } from './pages/WebhookLogs';
import { AdminSettings } from './pages/AdminSettings';
import { SupervisorReports } from './pages/SupervisorReports';
import { Financial } from './pages/Financial';
import { Login } from './pages/Login';
import DeveloperLogin from './pages/DeveloperLogin';
import DeveloperDashboard from './pages/DeveloperDashboard';
import { InternalChat } from './components/InternalChat';
import { getMe } from './lib/api';

function App() {
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);
  const [activePage, setActivePage] = useState('conversations');
  const [showInternalChat, setShowInternalChat] = useState(false);
  const internalChatRef = useRef(null);
  const [showDeveloperLogin, setShowDeveloperLogin] = useState(false);
  const [isMobile, setIsMobile] = useState(window.innerWidth < 768);
  const [selectedConversationId, setSelectedConversationId] = useState(null);

  // Check for mobile on mount and resize
  useEffect(() => {
    const handleResize = () => {
      const mobile = window.innerWidth < 768;
      setIsMobile(mobile);
      if (mobile && activePage !== 'conversations') {
        setActivePage('conversations');
      }
    };

    window.addEventListener('resize', handleResize);
    // Initial check
    if (isMobile && activePage !== 'conversations') {
      setActivePage('conversations');
    }

    return () => window.removeEventListener('resize', handleResize);
  }, [isMobile, activePage]);

  // Check if user is logged in on mount
  useEffect(() => {
    const checkAuth = async () => {
      const token = localStorage.getItem('token');
      const savedUser = localStorage.getItem('user');

      if (token && savedUser) {
        try {
          // Verify token is still valid
          const response = await getMe();
          setUser(response.data);
        } catch (error) {
          // Token invalid, clear storage
          localStorage.removeItem('token');
          localStorage.removeItem('user');
          setUser(null);
        }
      }
      setLoading(false);
    };

    checkAuth();
  }, []);

  const handleLogin = (userData) => {
    setUser(userData);
    setShowDeveloperLogin(false);
    // Set initial page based on role or device
    if (window.innerWidth < 768) {
      setActivePage('conversations');
    } else if (userData.role === 'developer') {
      setActivePage('developer');
    } else if (userData.role === 'admin') {
      setActivePage('dashboard');
    } else {
      setActivePage('conversations');
    }
  };

  const handleLogout = () => {
    localStorage.removeItem('token');
    localStorage.removeItem('user');
    setUser(null);
    setShowDeveloperLogin(false);
  };

  const handlePageChange = (page) => {
    const userRole = user?.role;

    // Define allowed pages per role
    const allowedPages = {
      admin: ['dashboard', 'conversations', 'contacts', 'reports', 'attendants', 'instances', 'integrations', 'settings', 'logs', 'financial'],
      supervisor: ['conversations', 'contacts', 'reports'],
      attendant: ['conversations', 'contacts']
    };

    const allowed = allowedPages[userRole] || ['conversations'];
    if (!allowed.includes(page)) {
      return;
    }
    setActivePage(page);
  };

  const renderPage = useCallback(() => {
    // Developer sees developer dashboard
    if (user?.role === 'developer') {
      return <DeveloperDashboard user={user} onLogout={handleLogout} />;
    }

    // Supervisor can access reports and conversations
    if (user?.role === 'supervisor') {
      switch (activePage) {
        case 'reports':
          return <SupervisorReports />;
        case 'contacts':
          return <Contacts
            currentUser={user}
            isMobile={isMobile}
            onNavigateToConversation={(id) => {
              setSelectedConversationId(id);
              setActivePage('conversations');
            }}
          />;
        case 'conversations':
        default:
          return <Conversations currentUser={user} isMobile={isMobile} />;
      }
    }

    // Attendants can see conversations and contacts
    if (user?.role === 'attendant') {
      switch (activePage) {
        case 'contacts':
          return <Contacts
            currentUser={user}
            isMobile={isMobile}
            onNavigateToConversation={(id) => {
              setSelectedConversationId(id);
              setActivePage('conversations');
            }}
          />;
        case 'conversations':
        default:
          return <Conversations currentUser={user} isMobile={isMobile} />;
      }
    }

    // Admin can see everything
    switch (activePage) {
      case 'dashboard':
        return <Dashboard onNavigate={(page, convId) => {
          setActivePage(page);
          if (convId) {
            setSelectedConversationId(convId);
          }
        }} />;
      case 'conversations':
        return <Conversations currentUser={user} initialSelectedId={selectedConversationId} isMobile={isMobile} />;
      case 'contacts':
        return <Contacts
          currentUser={user}
          isMobile={isMobile}
          onNavigateToConversation={(id) => {
            setSelectedConversationId(id);
            setActivePage('conversations');
          }}
        />;
      case 'reports':
        return <SupervisorReports />;
      case 'attendants':
        return <Attendants />;
      case 'instances':
        return <InstancesEvolution currentUser={user} />;
      case 'integrations':
        return <Integrations />;
      case 'financial':
        return <Financial />;
      case 'settings':
        return <AdminSettings />;
      case 'logs':
        return <WebhookLogs />;
      default:
        return <Conversations currentUser={user} isMobile={isMobile} />;
    }
  }, [activePage, user, isMobile, selectedConversationId]);

  if (loading) {
    return (
      <div className="h-screen w-screen bg-background flex items-center justify-center">
        <div className="w-8 h-8 border-2 border-primary border-t-transparent rounded-full animate-spin" />
      </div>
    );
  }

  // Not logged in - show login screens
  if (!user) {
    if (showDeveloperLogin) {
      return (
        <>
          <DeveloperLogin
            onLogin={handleLogin}
            onBack={() => setShowDeveloperLogin(false)}
          />
          <Toaster position="top-right" richColors theme="dark" />
        </>
      );
    }

    return (
      <>
        <Login
          onLogin={handleLogin}
          onDeveloperLogin={() => setShowDeveloperLogin(true)}
        />
        <Toaster position="top-right" richColors theme="dark" />
      </>
    );
  }

  // Developer sees full-screen dashboard (no sidebar)
  if (user.role === 'developer') {
    return (
      <>
        {renderPage()}
        <Toaster position="top-right" richColors theme="dark" />
      </>
    );
  }

  // Admin/Attendant sees normal layout with sidebar (hidden on mobile)
  return (
    <div className="app-container flex h-screen overflow-hidden" data-testid="app-container">
      {!isMobile && (
        <Sidebar
          activePage={activePage}
          onPageChange={handlePageChange}
          user={user}
          onLogout={handleLogout}
        />
      )}
      <main className="main-content flex-1 flex flex-col overflow-hidden">
        {renderPage()}
      </main>

      {/* Internal Team Chat */}
      <InternalChat
        isOpen={showInternalChat}
        onToggle={() => setShowInternalChat(!showInternalChat)}
        currentUser={user}
        onNewMessage={(handler) => { internalChatRef.current = handler; }}
      />

      <Toaster
        position="top-right"
        richColors
        theme="dark"
        toastOptions={{
          style: {
            background: 'hsl(240 6% 7%)',
            border: '1px solid hsl(240 4% 16%)',
          },
        }}
      />
    </div>
  );
}

export default App;
